import { useEffect, useState, useRef } from 'react'
import { useParams, useNavigate, useSearchParams } from 'react-router-dom'
import { AppShell } from '../components/layout/AppShell'
import { orgService } from '../services/orgService'
import { isoToLocalInput, localInputToISO } from '../utils/formatTime'
import type { Race, Team, Division, Category } from '../types/index'

type Tab = 'teams' | 'course' | 'broadcast'

export function OrgRacePage() {
  const { raceId } = useParams<{ raceId: string }>()
  const navigate = useNavigate()
  const id = Number(raceId)

  const [searchParams] = useSearchParams()
  const [race, setRace] = useState<Race | null>(null)
  const [teams, setTeams] = useState<Team[]>([])
  const [tab, setTab] = useState<Tab>((searchParams.get('tab') as Tab) ?? 'teams')
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Create / edit team
  const [creatingTeam, setCreatingTeam] = useState(false)
  const [editingTeam, setEditingTeam] = useState<Team | null>(null)
  const [newTeamName, setNewTeamName] = useState('')
  const [newTeamBib, setNewTeamBib] = useState('')
  const [newTeamDivision, setNewTeamDivision] = useState<Division | ''>('')
  const [newTeamCategory, setNewTeamCategory] = useState<Category | ''>('')
  const [newTeamSize, setNewTeamSize] = useState<6 | 12>(12)
  const [newTeamActual, setNewTeamActual] = useState('')
  const [newTeamStartTime, setNewTeamStartTime] = useState('')
  const [newCoachFirstName, setNewCoachFirstName] = useState('')
  const [newCoachLastName, setNewCoachLastName] = useState('')
  const [newCoachPhone, setNewCoachPhone] = useState('')
  const [newCoachEmail, setNewCoachEmail] = useState('')

  // Team search/filter/sort
  const [teamSearch, setTeamSearch] = useState('')
  const [teamDivisionFilter, setTeamDivisionFilter] = useState<string>('all')
  const [teamSort, setTeamSort] = useState<'bib' | 'name' | 'startTime'>('bib')
  const [teamSortDir, setTeamSortDir] = useState<'asc' | 'desc'>('asc')

  // Roster modal
  const [rosterTeam, setRosterTeam] = useState<Team | null>(null)
  const [rosterRunners, setRosterRunners] = useState<Array<{ id: number; name: string; vanNumber: number; runnerNumber: number; phoneNumber: string | null; status: string }>>([])
  const [rosterLegs, setRosterLegs] = useState<Array<{ id: number; legNumber: number; runnerId: number | null; runnerName: string | null }>>([])
  const [rosterLoading, setRosterLoading] = useState(false)

  const openRoster = async (team: Team) => {
    setRosterTeam(team)
    setRosterRunners([])
    setRosterLegs([])
    setRosterLoading(true)
    try {
      const [runners, legs] = await Promise.all([
        orgService.getTeamRunners(team.id),
        orgService.getTeamLegs(team.id),
      ])
      setRosterRunners(runners)
      setRosterLegs(legs)
    } catch { /* silent — show empty state */ }
    finally { setRosterLoading(false) }
  }

  // Mass start time update
  const [delayModalOpen, setDelayModalOpen] = useState(false)
  const [delayMode, setDelayMode] = useState<'offset' | 'set'>('offset')
  const [delayMinutes, setDelayMinutes] = useState('30')
  const [delayNewTime, setDelayNewTime] = useState('')
  const [delayDivision, setDelayDivision] = useState<string>('all')
  const [delaySelectedIds, setDelaySelectedIds] = useState<Set<number>>(new Set())
  const [delaySelectionMode, setDelaySelectionMode] = useState<'all' | 'division' | 'custom'>('all')
  const [delayApplying, setDelayApplying] = useState(false)
  const [delayError, setDelayError] = useState<string | null>(null)

  // Broadcast
  const [broadcastMsg, setBroadcastMsg] = useState('')
  const [broadcastResult, setBroadcastResult] = useState<{ sent: number; failed: number; total: number; pushSent?: number } | null>(null)
  const [broadcasting, setBroadcasting] = useState(false)
  const [smsCoverage, setSmsCoverage] = useState<any[]>([])
  const [coverageLoading, setCoverageLoading] = useState(false)
  const [expandedTeam, setExpandedTeam] = useState<number | null>(null)
  const [broadcastHistory, setBroadcastHistory] = useState<any[]>([])
  const [historyLoading, setHistoryLoading] = useState(false)

  const scrollRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!id) return
    Promise.all([orgService.getRace(id), orgService.getTeams(id)])
      .then(([r, t]) => { setRace(r); setTeams(t) })
      .catch(() => setError('Failed to load race data'))
      .finally(() => setLoading(false))
  }, [id])

  const raceMaxTeamSize = race?.maxTeamSize ?? 12
  const raceAllowedSizes = race?.allowedTeamSizes ?? [12, 6]

  const resetTeamForm = () => {
    setNewTeamName(''); setNewTeamBib(''); setNewTeamDivision('')
    setNewTeamCategory(''); setNewTeamSize(raceMaxTeamSize as 6 | 12); setNewTeamActual(''); setNewTeamStartTime('')
    setNewCoachFirstName(''); setNewCoachLastName(''); setNewCoachPhone(''); setNewCoachEmail('')
  }

  const handleCreateTeam = async () => {
    if (!newTeamName.trim()) return
    if (!newCoachEmail.trim()) return
    try {
      const team = await orgService.createTeam(id, {
        name: newTeamName.trim(),
        bibNumber: newTeamBib.trim() ? parseInt(newTeamBib.trim()) : undefined,
        division: newTeamDivision || undefined,
        category: newTeamCategory || undefined,
        teamSize: newTeamSize,
        runnerCountActual: newTeamActual.trim() ? parseInt(newTeamActual.trim()) : undefined,
        startTime: localInputToISO(newTeamStartTime, tz),
        coachFirstName: newCoachFirstName.trim() || undefined,
        coachLastName: newCoachLastName.trim() || undefined,
        coachPhone: newCoachPhone.trim() || undefined,
        coachEmail: newCoachEmail.trim() || undefined,
      })
      setTeams(prev => [...prev, team])
      resetTeamForm()
      setCreatingTeam(false)
    } catch {
      setError('Failed to create team')
    }
  }

  const openEditTeam = (team: Team) => {
    setEditingTeam(team)
    setNewTeamName(team.name)
    setNewTeamBib(team.bibNumber != null ? String(team.bibNumber) : '')
    setNewTeamDivision(team.division ?? '')
    setNewTeamCategory(team.category ?? '')
    setNewTeamSize((team.teamSize as 6 | 12) ?? (raceMaxTeamSize as 6 | 12))
    setNewTeamActual(team.runnerCountActual != null ? String(team.runnerCountActual) : '')
    setNewTeamStartTime(isoToLocalInput(team.startTime, tz))
    setNewCoachFirstName(team.coachFirstName ?? '')
    setNewCoachLastName(team.coachLastName ?? '')
    setNewCoachPhone(team.coachPhone ?? '')
    setNewCoachEmail(team.coachEmail ?? '')
  }

  const handleUpdateTeam = async () => {
    if (!editingTeam || !newTeamName.trim()) return
    try {
      const updated = await orgService.updateTeam(editingTeam.id, {
        name: newTeamName.trim(),
        bibNumber: newTeamBib.trim() ? parseInt(newTeamBib.trim()) : null,
        division: newTeamDivision || null,
        category: newTeamCategory || null,
        teamSize: newTeamSize,
        runnerCountActual: newTeamActual.trim() ? parseInt(newTeamActual.trim()) : null,
        startTime: localInputToISO(newTeamStartTime, tz),
        coachFirstName: newCoachFirstName.trim() || null,
        coachLastName: newCoachLastName.trim() || null,
        coachPhone: newCoachPhone.trim() || null,
        coachEmail: newCoachEmail.trim() || null,
      })
      setTeams(prev => prev.map(t => t.id === updated.id ? updated : t))
      resetTeamForm()
      setEditingTeam(null)
    } catch {
      setError('Failed to update team')
    }
  }


  const handleDeleteTeam = async (team: Team) => {
    if (!window.confirm(`Delete "${team.name}"? This will remove all runners and race data for this team.`)) return
    try {
      await orgService.deleteTeam(team.id)
      setTeams(prev => prev.filter(t => t.id !== team.id))
      resetTeamForm()
      setEditingTeam(null)
    } catch {
      setError('Failed to delete team')
    }
  }

  const tz = race?.timezone ?? 'America/Los_Angeles'

  const openDelayModal = () => {
    setDelayMode('offset')
    setDelayMinutes('30')
    setDelayNewTime('')
    setDelayDivision('all')
    setDelaySelectionMode('all')
    setDelaySelectedIds(new Set(teams.map(t => t.id)))
    setDelayError(null)
    setDelayModalOpen(true)
  }

  const delayAffectedTeams = (() => {
    if (delaySelectionMode === 'all') return teams
    if (delaySelectionMode === 'division') return teams.filter(t => t.division === delayDivision)
    return teams.filter(t => delaySelectedIds.has(t.id))
  })()

  const previewNewTime = (team: { startTime?: string | null }) => {
    if (delayMode === 'offset') {
      const mins = parseInt(delayMinutes) || 0
      if (!team.startTime) return null
      return new Date(new Date(team.startTime).getTime() + mins * 60000).toISOString()
    }
    return localInputToISO(delayNewTime, tz)
  }

  const applyDelay = async () => {
    setDelayError(null)
    if (delayMode === 'set' && !delayNewTime) { setDelayError('Please enter a new start time.'); return }
    if (delayMode === 'offset') {
      const mins = parseInt(delayMinutes)
      if (isNaN(mins) || mins === 0) { setDelayError('Enter a non-zero offset in minutes.'); return }
    }
    const affected = delayAffectedTeams
    if (affected.length === 0) { setDelayError('No teams selected.'); return }
    setDelayApplying(true)
    try {
      const updated = await Promise.all(
        affected.map(t => {
          const newIso = previewNewTime(t)
          return orgService.updateTeam(t.id, { startTime: newIso })
        })
      )
      setTeams(prev => prev.map(t => {
        const u = updated.find(u => u.id === t.id)
        return u ?? t
      }))
      setDelayModalOpen(false)
    } catch {
      setDelayError('Failed to update start times. Please try again.')
    } finally {
      setDelayApplying(false)
    }
  }

  const TABS: { key: Tab; label: string }[] = [
    { key: 'teams', label: 'Teams' },
    { key: 'course', label: 'Course' },
    { key: 'broadcast', label: 'Broadcast' },
  ]

  if (loading) return (
    <AppShell><div className="h-full flex items-center justify-center text-gray-400">Loading...</div></AppShell>
  )

  return (
    <AppShell>
      <div className="h-full flex flex-col overflow-hidden">
        {/* Header */}
        <div className="px-4 pt-3 pb-2 flex-shrink-0 border-b border-gray-200">
          <button onClick={() => navigate('/org')} className="text-xs text-indigo-600 mb-1 flex items-center gap-1">
            <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" /></svg>
            All Races
          </button>
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-lg font-bold">{race?.name}</h1>
              <p className="text-xs text-gray-500">
                {race?.eventDate ? new Date(race.eventDate).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }) : 'No date'}
                {' · '}{teams.length} team{teams.length !== 1 ? 's' : ''}
              </p>
            </div>
          </div>

          {/* Tabs */}
          <div className="flex gap-1 mt-2">
            {TABS.map(t => (
              <button
                key={t.key}
                onClick={() => setTab(t.key)}
                className={`px-3 py-1 text-sm rounded-md font-medium transition-colors ${
                  tab === t.key ? 'bg-indigo-600 text-white' : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>
        </div>

        {error && (
          <div className="mx-4 mt-2 bg-red-50 border border-red-200 text-red-700 text-sm px-3 py-2 rounded-lg flex-shrink-0">
            {error}
          </div>
        )}

        {/* Tab content */}
        <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3 min-h-0">

          {/* ── Teams tab ── */}
          {tab === 'teams' && (() => {
            const teamDivisions = Array.from(new Set(teams.map(t => t.division).filter(Boolean))) as string[]
            const toggleTeamSort = (field: typeof teamSort) => {
              if (teamSort === field) setTeamSortDir(d => d === 'asc' ? 'desc' : 'asc')
              else { setTeamSort(field); setTeamSortDir('asc') }
            }
            const visibleTeams = teams
              .filter(t => {
                if (teamSearch) {
                  const q = teamSearch.toLowerCase()
                  if (!t.name.toLowerCase().includes(q) &&
                      !String(t.bibNumber ?? '').includes(q) &&
                      !(t.division ?? '').toLowerCase().includes(q) &&
                      !(t.coachEmail ?? '').toLowerCase().includes(q)) return false
                }
                if (teamDivisionFilter !== 'all' && t.division !== teamDivisionFilter) return false
                return true
              })
              .sort((a, b) => {
                let cmp = 0
                if (teamSort === 'bib') cmp = (a.bibNumber ?? 9999) - (b.bibNumber ?? 9999)
                else if (teamSort === 'name') cmp = a.name.localeCompare(b.name)
                else {
                  const ta = a.startTime ? new Date(a.startTime).getTime() : Infinity
                  const tb = b.startTime ? new Date(b.startTime).getTime() : Infinity
                  cmp = ta - tb
                }
                return teamSortDir === 'asc' ? cmp : -cmp
              })

            return (
              <>
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2 flex-1 min-w-0">
                    <input
                      type="text"
                      value={teamSearch}
                      onChange={e => setTeamSearch(e.target.value)}
                      placeholder="Search name, bib, email…"
                      className="flex-1 min-w-0 px-2 py-1 text-xs border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-indigo-500"
                    />
                    {teamDivisions.length > 0 && (
                      <select
                        value={teamDivisionFilter}
                        onChange={e => setTeamDivisionFilter(e.target.value)}
                        className="text-xs border border-gray-300 rounded-md px-1.5 py-1 focus:outline-none focus:ring-1 focus:ring-indigo-500 flex-shrink-0"
                      >
                        <option value="all">All divisions</option>
                        {teamDivisions.sort().map(d => <option key={d} value={d}>{d}</option>)}
                      </select>
                    )}
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <button
                      onClick={openDelayModal}
                      className="px-3 py-1 bg-amber-500 text-white text-xs rounded-lg hover:bg-amber-600"
                    >
                      ⏱ Delay Start
                    </button>
                    <button
                      onClick={() => { setNewTeamSize(raceMaxTeamSize as 6 | 12); setCreatingTeam(true) }}
                      className="px-3 py-1 bg-indigo-600 text-white text-xs rounded-lg hover:bg-indigo-700"
                    >
                      + Add Team
                    </button>
                  </div>
                </div>

                {/* Sort bar */}
                <div className="flex items-center gap-1 text-xs text-gray-500">
                  <span className="mr-1">Sort:</span>
                  {(['bib', 'name', 'startTime'] as const).map(f => (
                    <button
                      key={f}
                      onClick={() => toggleTeamSort(f)}
                      className={`px-2 py-0.5 rounded font-medium transition-colors ${teamSort === f ? 'bg-indigo-100 text-indigo-700' : 'hover:bg-gray-100'}`}
                    >
                      {f === 'bib' ? '#' : f === 'name' ? 'Name' : 'Start'}{teamSort === f ? (teamSortDir === 'asc' ? ' ↑' : ' ↓') : ''}
                    </button>
                  ))}
                  <span className="ml-auto text-gray-400">{visibleTeams.length} of {teams.length}</span>
                </div>

                {visibleTeams.length === 0 ? (
                  <p className="text-sm text-gray-400 text-center py-8">
                    {teams.length === 0 ? 'No teams yet. Add the first team to get started.' : 'No teams match'}
                  </p>
                ) : (
                  <div className="space-y-2">
                    {visibleTeams.map(team => (
                      <div
                        key={team.id}
                        className="bg-white rounded-lg border border-gray-200 px-4 py-3"
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <div className="flex items-center gap-2 flex-wrap">
                              {team.bibNumber != null && (
                                <span className="text-xs font-mono font-bold bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded">#{team.bibNumber}</span>
                              )}
                              <p className="font-semibold text-gray-900">{team.name}</p>
                              {team.division && (
                                <span className="text-xs bg-indigo-50 text-indigo-700 px-1.5 py-0.5 rounded font-medium">{team.division}</span>
                              )}
                              {team.category && (
                                <span className="text-xs bg-purple-50 text-purple-700 px-1.5 py-0.5 rounded font-medium">{team.category}</span>
                              )}
                            </div>
                            <p className="text-xs text-gray-500 mt-0.5">
                              Join code: <span className="font-mono font-bold tracking-widest text-indigo-700">{team.joinCode}</span>
                              {' · '}{team.teamSize ?? 12}-person
                              {team.runnerCountActual != null && <span className="ml-1 text-amber-600">({team.runnerCountActual} actual)</span>}
                              {team.runnerCount !== undefined && (
                                <button
                                  onClick={() => openRoster(team)}
                                  className="ml-1 text-indigo-600 hover:underline"
                                >· {team.runnerCount} registered</button>
                              )}
                              {team.startTime && (
                                <span className="ml-1">· {new Date(team.startTime).toLocaleTimeString('en-US', { timeZone: tz, hour: 'numeric', minute: '2-digit', hour12: true })}</span>
                              )}
                            </p>
                            {(team.coachFirstName || team.coachEmail) && (
                              <p className="text-xs text-gray-400 mt-0.5">
                                Coach: {[team.coachFirstName, team.coachLastName].filter(Boolean).join(' ')}
                                {team.coachEmail && <span className="ml-1 font-mono">· {team.coachEmail}</span>}
                              </p>
                            )}
                          </div>
                          <button
                            onClick={() => openEditTeam(team)}
                            className="text-xs text-indigo-600 hover:underline flex-shrink-0"
                          >Edit</button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )
          })()}

          {/* ── Course tab ── */}
          {tab === 'course' && (
            <>
              <div className="flex items-center justify-between">
                <h2 className="font-semibold text-gray-700">Course Legs</h2>
                <button
                  onClick={() => navigate(`/org/races/${id}/legs/1`)}
                  className="px-3 py-1 bg-indigo-600 text-white text-xs rounded-lg hover:bg-indigo-700"
                >
                  Edit Legs
                </button>
              </div>
              <CourseLegList raceId={id} onEdit={legId => navigate(`/org/races/${id}/legs/${legId}`)} />
            </>
          )}

          {/* ── Broadcast tab ── */}
          {tab === 'broadcast' && <BroadcastTab
            raceId={id}
            timezone={tz}
            broadcastMsg={broadcastMsg}
            setBroadcastMsg={setBroadcastMsg}
            broadcastResult={broadcastResult}
            setBroadcastResult={setBroadcastResult}
            broadcasting={broadcasting}
            setBroadcasting={setBroadcasting}
            smsCoverage={smsCoverage}
            setSmsCoverage={setSmsCoverage}
            coverageLoading={coverageLoading}
            setCoverageLoading={setCoverageLoading}
            expandedTeam={expandedTeam}
            setExpandedTeam={setExpandedTeam}
            broadcastHistory={broadcastHistory}
            setBroadcastHistory={setBroadcastHistory}
            historyLoading={historyLoading}
            setHistoryLoading={setHistoryLoading}
            setError={setError}
          />}

        </div>
      </div>

      {/* Roster modal */}
      {rosterTeam && (() => {
        // Build a map of runnerId → legs assigned
        const legsByRunner = new Map<number, number[]>()
        for (const leg of rosterLegs) {
          if (leg.runnerId != null) {
            const arr = legsByRunner.get(leg.runnerId) ?? []
            arr.push(leg.legNumber)
            legsByRunner.set(leg.runnerId, arr)
          }
        }
        const unassignedLegs = rosterLegs.filter(l => l.runnerId == null).map(l => l.legNumber)
        return (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg p-5 w-full max-w-md space-y-3 max-h-[85vh] flex flex-col">
              <div className="flex items-start justify-between flex-shrink-0">
                <div>
                  <h2 className="text-lg font-bold">{rosterTeam.name}</h2>
                  <p className="text-xs text-gray-500">
                    {rosterTeam.teamSize ?? 12}-person · {rosterRunners.length} registered
                    {rosterTeam.runnerCountActual != null && ` · ${rosterTeam.runnerCountActual} actual`}
                  </p>
                </div>
                <button onClick={() => setRosterTeam(null)} className="text-gray-400 hover:text-gray-600 text-xl leading-none ml-4">✕</button>
              </div>

              {rosterLoading ? (
                <p className="text-sm text-gray-400 text-center py-6">Loading…</p>
              ) : rosterRunners.length === 0 ? (
                <p className="text-sm text-gray-400 text-center py-6">No runners registered yet.</p>
              ) : (
                <div className="overflow-y-auto flex-1 min-h-0 -mx-1">
                  <table className="w-full text-xs">
                    <thead className="sticky top-0 bg-white">
                      <tr className="border-b border-gray-200">
                        <th className="text-left px-2 py-1.5 text-gray-500 font-semibold w-6">#</th>
                        <th className="text-left px-2 py-1.5 text-gray-500 font-semibold">Name</th>
                        <th className="text-left px-2 py-1.5 text-gray-500 font-semibold w-10">Van</th>
                        <th className="text-left px-2 py-1.5 text-gray-500 font-semibold">Legs</th>
                        <th className="text-left px-2 py-1.5 text-gray-500 font-semibold">Phone</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rosterRunners
                        .slice()
                        .sort((a, b) => a.runnerNumber - b.runnerNumber)
                        .map(r => {
                          const legs = legsByRunner.get(r.id) ?? []
                          return (
                            <tr key={r.id} className="border-b border-gray-50 hover:bg-gray-50">
                              <td className="px-2 py-2 font-mono text-gray-400">{r.runnerNumber}</td>
                              <td className="px-2 py-2 font-medium text-gray-900">{r.name}</td>
                              <td className="px-2 py-2 text-gray-500">{r.vanNumber}</td>
                              <td className="px-2 py-2 text-gray-600">
                                {legs.length > 0
                                  ? legs.sort((a, b) => a - b).join(', ')
                                  : <span className="text-gray-300">—</span>}
                              </td>
                              <td className="px-2 py-2 text-gray-500 font-mono">
                                {r.phoneNumber ?? <span className="text-gray-300">—</span>}
                              </td>
                            </tr>
                          )
                        })}
                    </tbody>
                  </table>
                </div>
              )}

              {!rosterLoading && unassignedLegs.length > 0 && (
                <p className="text-xs text-amber-600 bg-amber-50 border border-amber-200 rounded px-3 py-2 flex-shrink-0">
                  {unassignedLegs.length} leg{unassignedLegs.length !== 1 ? 's' : ''} unassigned: {unassignedLegs.sort((a, b) => a - b).join(', ')}
                </p>
              )}

              <button
                onClick={() => setRosterTeam(null)}
                className="w-full py-2 border border-gray-300 text-gray-700 text-sm rounded-lg hover:bg-gray-50 flex-shrink-0"
              >Close</button>
            </div>
          </div>
        )
      })()}

      {/* Delay Start Times modal */}
      {delayModalOpen && (() => {
        const teamDivisions = Array.from(new Set(teams.map(t => t.division).filter(Boolean))) as string[]
        const affected = delayAffectedTeams
        const formatPT = (iso: string | null | undefined) => {
          if (!iso) return '—'
          return new Date(iso).toLocaleTimeString('en-US', { timeZone: tz, hour: 'numeric', minute: '2-digit', hour12: true, month: 'short', day: 'numeric' })
        }
        return (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg p-5 w-full max-w-lg space-y-4 max-h-[90vh] overflow-y-auto">
              <h2 className="text-lg font-bold">Delay Start Times</h2>

              {/* Mode */}
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1.5">Update type</label>
                <div className="flex gap-2">
                  <button
                    onClick={() => setDelayMode('offset')}
                    className={`flex-1 py-1.5 text-xs rounded-lg border font-medium ${delayMode === 'offset' ? 'bg-amber-500 text-white border-amber-500' : 'border-gray-300 text-gray-600 hover:bg-gray-50'}`}
                  >Add delay (minutes)</button>
                  <button
                    onClick={() => setDelayMode('set')}
                    className={`flex-1 py-1.5 text-xs rounded-lg border font-medium ${delayMode === 'set' ? 'bg-amber-500 text-white border-amber-500' : 'border-gray-300 text-gray-600 hover:bg-gray-50'}`}
                  >Set to specific time</button>
                </div>
              </div>

              {/* Time input */}
              {delayMode === 'offset' ? (
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">Delay (minutes)</label>
                  <input
                    type="number"
                    value={delayMinutes}
                    onChange={e => setDelayMinutes(e.target.value)}
                    placeholder="30"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-500"
                  />
                  <p className="text-xs text-gray-400 mt-1">Use negative minutes to move start times earlier.</p>
                </div>
              ) : (
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">New start time</label>
                  <input
                    type="datetime-local"
                    value={delayNewTime}
                    onChange={e => setDelayNewTime(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-500"
                  />
                  <p className="text-xs text-gray-400 mt-1">All selected teams will be set to this exact time.</p>
                </div>
              )}

              {/* Team selection */}
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1.5">Apply to</label>
                <div className="flex gap-2 flex-wrap">
                  {(['all', 'division', 'custom'] as const).map(mode => (
                    <button
                      key={mode}
                      onClick={() => {
                        setDelaySelectionMode(mode)
                        if (mode === 'all') setDelaySelectedIds(new Set(teams.map(t => t.id)))
                        if (mode === 'division' && teamDivisions.length > 0) setDelayDivision(teamDivisions[0])
                      }}
                      className={`px-3 py-1 text-xs rounded-lg border font-medium ${delaySelectionMode === mode ? 'bg-indigo-600 text-white border-indigo-600' : 'border-gray-300 text-gray-600 hover:bg-gray-50'}`}
                    >
                      {mode === 'all' ? 'All teams' : mode === 'division' ? 'By division' : 'Custom'}
                    </button>
                  ))}
                </div>

                {delaySelectionMode === 'division' && teamDivisions.length > 0 && (
                  <select
                    value={delayDivision}
                    onChange={e => setDelayDivision(e.target.value)}
                    className="mt-2 text-xs border border-gray-300 rounded-md px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-indigo-500 w-full"
                  >
                    {teamDivisions.sort().map(d => <option key={d} value={d}>{d}</option>)}
                  </select>
                )}

                {delaySelectionMode === 'custom' && (
                  <div className="mt-2 border border-gray-200 rounded-lg overflow-hidden max-h-40 overflow-y-auto">
                    {teams.sort((a, b) => (a.bibNumber ?? 9999) - (b.bibNumber ?? 9999)).map(t => (
                      <label key={t.id} className="flex items-center gap-2 px-3 py-1.5 hover:bg-gray-50 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={delaySelectedIds.has(t.id)}
                          onChange={() => {
                            const next = new Set(delaySelectedIds)
                            if (next.has(t.id)) next.delete(t.id)
                            else next.add(t.id)
                            setDelaySelectedIds(next)
                          }}
                          className="w-3.5 h-3.5 accent-indigo-600"
                        />
                        <span className="text-xs text-gray-800">
                          {t.bibNumber != null && <span className="font-mono text-gray-500 mr-1">#{t.bibNumber}</span>}
                          {t.name}
                        </span>
                      </label>
                    ))}
                  </div>
                )}
              </div>

              {/* Preview */}
              {affected.length > 0 && (
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1.5">
                    Preview — {affected.length} team{affected.length !== 1 ? 's' : ''}
                  </label>
                  <div className="border border-gray-200 rounded-lg overflow-hidden max-h-48 overflow-y-auto">
                    <table className="w-full text-xs">
                      <thead className="bg-gray-50 sticky top-0">
                        <tr>
                          <th className="text-left px-3 py-1.5 text-gray-500 font-medium">Team</th>
                          <th className="text-left px-3 py-1.5 text-gray-500 font-medium">Current</th>
                          <th className="text-left px-3 py-1.5 text-gray-500 font-medium">New</th>
                        </tr>
                      </thead>
                      <tbody>
                        {affected.sort((a, b) => (a.bibNumber ?? 9999) - (b.bibNumber ?? 9999)).map(t => {
                          const newIso = previewNewTime(t)
                          const changed = newIso !== (t.startTime ?? null)
                          return (
                            <tr key={t.id} className="border-t border-gray-100">
                              <td className="px-3 py-1.5 text-gray-800">
                                {t.bibNumber != null && <span className="font-mono text-gray-400 mr-1">#{t.bibNumber}</span>}
                                {t.name}
                              </td>
                              <td className="px-3 py-1.5 text-gray-500">{formatPT(t.startTime)}</td>
                              <td className={`px-3 py-1.5 font-medium ${changed ? 'text-amber-700' : 'text-gray-400'}`}>
                                {formatPT(newIso)}
                              </td>
                            </tr>
                          )
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {delayError && (
                <p className="text-xs text-red-600 bg-red-50 border border-red-200 rounded px-3 py-2">{delayError}</p>
              )}

              <div className="flex gap-2 pt-1">
                <button
                  onClick={() => setDelayModalOpen(false)}
                  className="flex-1 py-2 border border-gray-300 text-gray-700 text-sm rounded-lg hover:bg-gray-50"
                  disabled={delayApplying}
                >Cancel</button>
                <button
                  onClick={applyDelay}
                  disabled={delayApplying || affected.length === 0}
                  className="flex-1 py-2 bg-amber-500 text-white text-sm rounded-lg hover:bg-amber-600 disabled:opacity-50 font-medium"
                >
                  {delayApplying ? 'Updating…' : `Update ${affected.length} team${affected.length !== 1 ? 's' : ''}`}
                </button>
              </div>
            </div>
          </div>
        )
      })()}

      {/* Create / Edit team modal */}
      {(creatingTeam || editingTeam) && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-5 w-full max-w-sm space-y-3 max-h-[90vh] overflow-y-auto">
            <h2 className="text-lg font-bold">{editingTeam ? 'Edit Team' : 'Add Team'}</h2>

            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1">Team Name *</label>
              <input type="text" value={newTeamName} onChange={e => setNewTeamName(e.target.value)}
                placeholder="Team TeziTech" autoFocus
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500" />
            </div>

            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1">Bib Number</label>
              <input type="number" value={newTeamBib} onChange={e => setNewTeamBib(e.target.value)}
                placeholder="e.g. 142"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500" />
            </div>

            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">Division</label>
                <select value={newTeamDivision}
                  onChange={e => {
                    const d = e.target.value as Division | ''
                    setNewTeamDivision(d)
                    if (d === 'Ultra') {
                      setNewTeamSize(6)
                      if (newTeamActual && parseInt(newTeamActual) > 6) setNewTeamActual('6')
                    } else if (d) {
                      setNewTeamSize(12)
                    }
                  }}
                  className="w-full px-2 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500">
                  <option value="">— Select —</option>
                  <option value="Open">Open</option>
                  <option value="Masters">Masters</option>
                  <option value="Seniors">Seniors</option>
                  <option value="Corporate">Corporate</option>
                  <option value="Club">Club</option>
                  <option value="Ultra">Ultra</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">Category</label>
                <select value={newTeamCategory} onChange={e => setNewTeamCategory(e.target.value as Category | '')}
                  className="w-full px-2 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500">
                  <option value="">— Select —</option>
                  <option value="Mens">Mens</option>
                  <option value="Mixed">Mixed</option>
                  <option value="Womens">Womens</option>
                </select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">Team Size</label>
                <select value={newTeamSize} onChange={e => {
                    const s = Number(e.target.value) as 6 | 12
                    setNewTeamSize(s)
                    if (newTeamActual && parseInt(newTeamActual) > s) setNewTeamActual(String(s))
                    if (s > 6) setNewTeamDivision(d => d === 'Ultra' ? '' : d)
                  }}
                  className="w-full px-2 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500">
                  {[...raceAllowedSizes].sort((a, b) => b - a).map(s => (
                    <option key={s} value={s}>{s} runners</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">Actual Runners</label>
                <input type="number" value={newTeamActual}
                  onChange={e => {
                    const v = e.target.value
                    if (v === '' || parseInt(v) <= newTeamSize) setNewTeamActual(v)
                  }}
                  placeholder={String(newTeamSize)} min={1} max={newTeamSize}
                  className="w-full px-2 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500" />
              </div>
            </div>

            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1">Start Time</label>
              <input
                type="datetime-local"
                value={newTeamStartTime}
                onChange={e => setNewTeamStartTime(e.target.value)}
                className="w-full px-2 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>

            <div className="border-t border-gray-100 pt-3">
              <p className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-2">Team Admin (Coach)</p>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">First Name</label>
                  <input type="text" value={newCoachFirstName} onChange={e => setNewCoachFirstName(e.target.value)}
                    placeholder="Jane"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">Last Name</label>
                  <input type="text" value={newCoachLastName} onChange={e => setNewCoachLastName(e.target.value)}
                    placeholder="Smith"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500" />
                </div>
              </div>
              <div className="mt-2">
                <label className="block text-xs font-semibold text-gray-600 mb-1">Email — required (used as login username) *</label>
                <input type="email" value={newCoachEmail} onChange={e => setNewCoachEmail(e.target.value)}
                  placeholder="jane@example.com"
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500" />
              </div>
              <div className="mt-2">
                <label className="block text-xs font-semibold text-gray-600 mb-1">Phone</label>
                <input type="tel" value={newCoachPhone} onChange={e => setNewCoachPhone(e.target.value)}
                  placeholder="+1 555-555-5555"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500" />
              </div>
            </div>

            {!editingTeam && <p className="text-xs text-gray-500">A unique join code will be generated automatically. A team admin account will be created using the coach email as the username, with the join code as the temporary password. Team admins manage their team via the mobile app.</p>}

            <div className="flex gap-2 pt-1">
              <button
                onClick={() => { resetTeamForm(); setCreatingTeam(false); setEditingTeam(null) }}
                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50"
              >Cancel</button>
              {editingTeam && (
                <button
                  onClick={() => handleDeleteTeam(editingTeam)}
                  className="px-3 py-2 bg-red-600 text-white text-sm rounded-lg hover:bg-red-700"
                >Delete</button>
              )}
              <button
                onClick={editingTeam ? handleUpdateTeam : handleCreateTeam}
                disabled={!newTeamName.trim() || (!editingTeam && !newCoachEmail.trim())}
                className="flex-1 px-3 py-2 bg-indigo-600 text-white text-sm rounded-lg hover:bg-indigo-700 disabled:opacity-50"
              >{editingTeam ? 'Save' : 'Add'}</button>
            </div>
          </div>
        </div>
      )}
    </AppShell>
  )
}

// ── Broadcast tab component ───────────────────────────────────────────────────

interface BroadcastTabProps {
  raceId: number
  timezone: string
  broadcastMsg: string
  setBroadcastMsg: (v: string) => void
  broadcastResult: { sent: number; failed: number; total: number; pushSent?: number } | null
  setBroadcastResult: (v: any) => void
  broadcasting: boolean
  setBroadcasting: (v: boolean) => void
  smsCoverage: any[]
  setSmsCoverage: (v: any[]) => void
  coverageLoading: boolean
  setCoverageLoading: (v: boolean) => void
  expandedTeam: number | null
  setExpandedTeam: (v: number | null) => void
  broadcastHistory: any[]
  setBroadcastHistory: (v: any[]) => void
  historyLoading: boolean
  setHistoryLoading: (v: boolean) => void
  setError: (v: string | null) => void
}

function BroadcastTab({
  raceId, timezone, broadcastMsg, setBroadcastMsg, broadcastResult, setBroadcastResult,
  broadcasting, setBroadcasting, smsCoverage, setSmsCoverage,
  coverageLoading, setCoverageLoading, expandedTeam, setExpandedTeam,
  broadcastHistory, setBroadcastHistory, historyLoading, setHistoryLoading, setError,
}: BroadcastTabProps) {
  const [coverageSearch, setCoverageSearch] = useState('')
  const [coverageDivision, setCoverageDivision] = useState<string>('all')
  const [coverageFilter, setCoverageFilter] = useState<'all' | 'missing'>('all')
  const [coverageSort, setCoverageSort] = useState<'bib' | 'name'>('bib')
  const [coverageSortDir, setCoverageSortDir] = useState<'asc' | 'desc'>('asc')

  useEffect(() => {
    setCoverageLoading(true)
    orgService.getSmsCoverage(raceId)
      .then(setSmsCoverage)
      .catch(() => setError('Failed to load SMS coverage'))
      .finally(() => setCoverageLoading(false))

    setHistoryLoading(true)
    orgService.getBroadcastHistory(raceId)
      .then(setBroadcastHistory)
      .catch(() => {})
      .finally(() => setHistoryLoading(false))
  }, [raceId])

  const handleBroadcast = async () => {
    if (!broadcastMsg.trim()) return
    setBroadcasting(true)
    try {
      const result = await orgService.sendBroadcast(raceId, broadcastMsg.trim(), true)
      setBroadcastResult(result)
      setBroadcastMsg('')
      orgService.getBroadcastHistory(raceId).then(setBroadcastHistory).catch(() => {})
    } catch {
      setError('Broadcast failed')
    } finally {
      setBroadcasting(false)
    }
  }

  const totalOptedIn = smsCoverage.reduce((sum, t) => sum + t.van1.optedIn + t.van2.optedIn, 0)
  const totalRunners = smsCoverage.reduce((sum, t) => sum + t.van1.total + t.van2.total, 0)
  const selectedTeam = expandedTeam !== null ? smsCoverage.find(t => t.teamId === expandedTeam) : null

  const coverageDivisions = Array.from(new Set(smsCoverage.map((t: any) => t.division).filter(Boolean))) as string[]

  const visibleCoverage = [...smsCoverage]
    .filter((t: any) => {
      if (coverageSearch) {
        const q = coverageSearch.toLowerCase()
        if (!t.teamName.toLowerCase().includes(q) && !String(t.bibNumber ?? '').includes(q)) return false
      }
      if (coverageDivision !== 'all' && t.division !== coverageDivision) return false
      if (coverageFilter === 'missing') return t.van1.optedIn === 0 || t.van2.optedIn === 0
      return true
    })
    .sort((a: any, b: any) => {
      const cmp = coverageSort === 'bib'
        ? (a.bibNumber ?? 9999) - (b.bibNumber ?? 9999)
        : a.teamName.localeCompare(b.teamName)
      return coverageSortDir === 'asc' ? cmp : -cmp
    })

  const toggleCoverageSort = (field: typeof coverageSort) => {
    if (coverageSort === field) setCoverageSortDir(d => d === 'asc' ? 'desc' : 'asc')
    else { setCoverageSort(field); setCoverageSortDir('asc') }
  }

  return (
    <div className="flex flex-col gap-2">

      {/* ── Summary bar ── */}
      <div className="flex items-center gap-3 text-xs flex-shrink-0">
        <span className="font-semibold text-gray-700">SMS Coverage</span>
        {!coverageLoading && <>
          <span className="px-2 py-0.5 bg-green-100 text-green-700 rounded-full font-semibold">{totalOptedIn}/{totalRunners} opted-in</span>
          <span className="text-gray-400">{visibleCoverage.length}{visibleCoverage.length !== smsCoverage.length ? `/${smsCoverage.length}` : ''} teams</span>
        </>}
      </div>

      {/* ── Search + filter bar ── */}
      {!coverageLoading && smsCoverage.length > 0 && (
        <div className="flex gap-1.5 flex-shrink-0">
          <input
            type="text"
            value={coverageSearch}
            onChange={e => setCoverageSearch(e.target.value)}
            placeholder="Search team or bib…"
            className="flex-1 min-w-0 px-2 py-1 text-xs border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-indigo-500"
          />
          {coverageDivisions.length > 0 && (
            <select
              value={coverageDivision}
              onChange={e => setCoverageDivision(e.target.value)}
              className="text-xs border border-gray-300 rounded-md px-1.5 py-1 focus:outline-none focus:ring-1 focus:ring-indigo-500 flex-shrink-0"
            >
              <option value="all">All divisions</option>
              {coverageDivisions.sort().map(d => <option key={d} value={d}>{d}</option>)}
            </select>
          )}
          <select
            value={coverageFilter}
            onChange={e => setCoverageFilter(e.target.value as any)}
            className="text-xs border border-gray-300 rounded-md px-1.5 py-1 focus:outline-none focus:ring-1 focus:ring-indigo-500 flex-shrink-0"
          >
            <option value="all">All</option>
            <option value="missing">Missing SMS</option>
          </select>
        </div>
      )}

      {/* ── Coverage table ── */}
      <div className="border border-gray-200 rounded-lg overflow-hidden flex-shrink-0 flex flex-col" style={{ maxHeight: '420px' }}>
        {coverageLoading ? (
          <p className="text-xs text-gray-400 text-center py-4">Loading...</p>
        ) : smsCoverage.length === 0 ? (
          <p className="text-xs text-gray-400 text-center py-4">No teams registered yet.</p>
        ) : (
          <div className="overflow-y-auto" style={{ maxHeight: '420px' }}>
            <table className="w-full text-xs">
              <thead className="bg-gray-50 sticky top-0 border-b border-gray-200">
                <tr>
                  <th
                    className="px-2 py-1.5 text-center font-semibold text-gray-500 w-10 cursor-pointer select-none hover:text-indigo-600"
                    onClick={() => toggleCoverageSort('bib')}
                  ># {coverageSort === 'bib' ? (coverageSortDir === 'asc' ? '↑' : '↓') : ''}</th>
                  <th
                    className="px-2 py-1.5 text-left font-semibold text-gray-500 cursor-pointer select-none hover:text-indigo-600"
                    onClick={() => toggleCoverageSort('name')}
                  >Team {coverageSort === 'name' ? (coverageSortDir === 'asc' ? '↑' : '↓') : ''}</th>
                  <th className="px-2 py-1.5 text-center font-semibold text-gray-500">Van 1</th>
                  <th className="px-2 py-1.5 text-center font-semibold text-gray-500">Van 2</th>
                </tr>
              </thead>
              <tbody>
                {visibleCoverage.map((team: any) => {
                  const hasGap = team.van1.optedIn === 0 || team.van2.optedIn === 0
                  return (
                    <tr
                      key={team.teamId}
                      onClick={() => setExpandedTeam(team.teamId)}
                      className={`border-t border-gray-100 cursor-pointer hover:bg-gray-50 ${hasGap ? 'bg-red-50' : ''}`}
                    >
                      <td className="px-2 py-1 text-center font-mono text-gray-400">{team.bibNumber ?? '—'}</td>
                      <td className="px-2 py-1">
                        <p className="font-medium text-gray-900 truncate max-w-[140px]">{team.teamName}</p>
                        {(team.division || team.category) && (
                          <p className="text-[10px] text-gray-400 leading-tight">{[team.division, team.category].filter(Boolean).join(' · ')}</p>
                        )}
                      </td>
                      <td className="px-2 py-1 text-center">
                        <span className={`font-bold ${team.van1.optedIn > 0 ? 'text-green-600' : 'text-red-500'}`}>
                          {team.van1.optedIn > 0 ? '✓' : '✕'} {team.van1.optedIn}/{team.van1.total}
                        </span>
                      </td>
                      <td className="px-2 py-1 text-center">
                        <span className={`font-bold ${team.van2.optedIn > 0 ? 'text-green-600' : 'text-red-500'}`}>
                          {team.van2.optedIn > 0 ? '✓' : '✕'} {team.van2.optedIn}/{team.van2.total}
                        </span>
                      </td>
                    </tr>
                  )
                })}
                {visibleCoverage.length === 0 && (
                  <tr><td colSpan={4} className="text-center py-4 text-xs text-gray-400">No teams match</td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ── Broadcast form ── */}
      <div className="space-y-2 flex-shrink-0">
        <textarea
          value={broadcastMsg}
          onChange={e => setBroadcastMsg(e.target.value)}
          rows={3}
          maxLength={320}
          placeholder="Lightning detected in the area. All runners please seek shelter immediately."
          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 resize-none"
        />
        <div className="flex items-center justify-between">
          <span className="text-xs text-gray-400">{broadcastMsg.length}/320</span>
          <button
            onClick={handleBroadcast}
            disabled={!broadcastMsg.trim() || broadcasting || totalOptedIn === 0}
            className="px-4 py-1.5 bg-red-600 text-white text-sm rounded-lg font-semibold hover:bg-red-700 disabled:opacity-50 transition-colors"
          >
            {broadcasting ? 'Sending...' : `Send to ${totalOptedIn} Runners`}
          </button>
        </div>
        {broadcastResult && (
          <div className="bg-green-50 border border-green-200 rounded-lg px-3 py-1.5 text-xs">
            <span className="font-semibold text-green-800">Sent · </span>
            <span className="text-green-700">
              SMS: {broadcastResult.sent} delivered · {broadcastResult.failed} failed
              {broadcastResult.pushSent != null && broadcastResult.pushSent > 0 && ` · Push: ${broadcastResult.pushSent} devices`}
            </span>
          </div>
        )}
      </div>

      {/* ── Broadcast history ── */}
      <div className="flex-shrink-0 flex flex-col" style={{ maxHeight: '160px' }}>
        <p className="text-xs font-semibold text-gray-500 mb-1.5">
          Sent Messages {!historyLoading && broadcastHistory.length > 0 && <span className="font-normal text-gray-400">({broadcastHistory.length})</span>}
        </p>
        {historyLoading ? (
          <p className="text-xs text-gray-400 text-center py-3">Loading...</p>
        ) : broadcastHistory.length === 0 ? (
          <p className="text-xs text-gray-400 text-center py-3 italic">No broadcasts sent yet.</p>
        ) : (
          <div className="overflow-y-auto space-y-1.5">
            {broadcastHistory.map(item => (
              <div key={item.id} className="border border-gray-200 rounded-lg px-3 py-2 text-xs bg-white">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-gray-400">
                    {new Date(item.sent_at).toLocaleString('en-US', { timeZone: timezone, month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true })}
                  </span>
                  <span className={`font-semibold ${item.failed_count > 0 ? 'text-amber-600' : 'text-green-600'}`}>
                    {item.sent_count}/{item.total_count} sent{item.failed_count > 0 ? ` · ${item.failed_count} failed` : ''}
                  </span>
                </div>
                <p className="text-gray-800 leading-snug">{item.message}</p>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ── Runner detail modal ── */}
      {selectedTeam && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50 p-4" onClick={() => setExpandedTeam(null)}>
          <div className="bg-white rounded-xl shadow-xl w-full max-w-sm p-4" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-3">
              <div>
                <div className="flex items-center gap-2">
                  {selectedTeam.bibNumber != null && (
                    <span className="text-xs font-mono font-bold bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded">#{selectedTeam.bibNumber}</span>
                  )}
                  <h3 className="font-bold text-gray-900">{selectedTeam.teamName}</h3>
                </div>
                <p className="text-xs text-gray-400 font-mono">{selectedTeam.joinCode}</p>
              </div>
              <button onClick={() => setExpandedTeam(null)} className="text-gray-400 hover:text-gray-600 text-lg leading-none">✕</button>
            </div>
            <div className="grid grid-cols-2 gap-4">
              {[1, 2].map(vanNum => {
                const van = vanNum === 1 ? selectedTeam.van1 : selectedTeam.van2
                return (
                  <div key={vanNum}>
                    <p className="text-xs font-semibold text-gray-500 mb-1.5 flex items-center gap-1">
                      Van {vanNum}
                      <span className={`ml-1 text-xs font-bold ${van.optedIn > 0 ? 'text-green-600' : 'text-red-500'}`}>
                        {van.optedIn > 0 ? '✓' : '✕'} {van.optedIn}/{van.total}
                      </span>
                    </p>
                    {van.runners.length === 0 ? (
                      <p className="text-xs text-gray-400 italic">No runners</p>
                    ) : van.runners.map((r: any) => (
                      <div key={r.id} className="flex items-start gap-1.5 py-0.5">
                        <span className={`flex-shrink-0 mt-0.5 w-3.5 h-3.5 rounded-full flex items-center justify-center text-white font-bold ${r.optedIn ? 'bg-green-500' : 'bg-red-400'}`} style={{ fontSize: 8 }}>
                          {r.optedIn ? '✓' : '✕'}
                        </span>
                        <div className="min-w-0">
                          <p className="text-xs font-medium text-gray-800 leading-tight">{r.name}</p>
                          <p className="text-xs text-gray-400 font-mono leading-tight">
                            {r.phoneNumber ?? <span className="italic text-gray-300">no phone</span>}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                )
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

// ── Inline course leg list component ─────────────────────────────────────────

function CourseLegList({ raceId, onEdit }: { raceId: number; onEdit: (legId: number) => void }) {
  const [legs, setLegs] = useState<any[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    orgService.getCourseLegs(raceId)
      .then(setLegs)
      .finally(() => setLoading(false))
  }, [raceId])

  if (loading) return <div className="text-sm text-gray-400 text-center py-4">Loading...</div>

  const totalMiles = legs.reduce((s, l) => s + (l.distanceMiles ?? 0), 0)
  const totalGain = legs.reduce((s, l) => s + (l.elevationGain ?? 0), 0)
  const totalLoss = legs.reduce((s, l) => s + (l.elevationLoss ?? 0), 0)

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-4 px-3 py-2 bg-gray-50 rounded-lg border border-gray-200 text-sm">
        <span className="font-semibold text-gray-600">{legs.length} legs</span>
        <span className="text-gray-300">|</span>
        <span className="font-mono font-semibold text-gray-700">{totalMiles.toFixed(1)} mi</span>
        <span className="text-gray-300">|</span>
        <span className="text-green-600 font-semibold">↑{totalGain.toLocaleString()} ft</span>
        <span className="text-orange-600 font-semibold">↓{totalLoss.toLocaleString()} ft</span>
      </div>
    <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
      <table className="w-full text-xs">
        <thead className="bg-gray-50 border-b border-gray-200">
          <tr>
            <th className="px-2 py-2 text-left font-semibold text-gray-500">Leg</th>
            <th className="px-2 py-2 text-left font-semibold text-gray-500">Start → End</th>
            <th className="px-2 py-2 text-center font-semibold text-gray-500">Mi</th>
            <th className="px-2 py-2 text-center font-semibold text-gray-500">Diff</th>
            <th className="px-2 py-2 text-center font-semibold text-gray-500">WPs</th>
          </tr>
        </thead>
        <tbody>
          {legs.map(leg => (
            <tr key={leg.id} onClick={() => onEdit(leg.id)} className="border-b border-gray-100 last:border-0 hover:bg-gray-50 cursor-pointer">
              <td className="px-2 py-1.5 font-bold text-gray-700">{leg.legNumber}</td>
              <td className="px-2 py-1.5 text-gray-600 max-w-[160px]">
                <p className="truncate">{leg.startAddress || `${leg.startLat.toFixed(4)}, ${leg.startLng.toFixed(4)}`}</p>
                <p className="truncate text-gray-400">{leg.endAddress || `${leg.endLat.toFixed(4)}, ${leg.endLng.toFixed(4)}`}</p>
              </td>
              <td className="px-2 py-1.5 text-center font-mono">{leg.distanceMiles?.toFixed(1)}</td>
              <td className="px-2 py-1.5 text-center">
                <span className={`px-1.5 py-0.5 rounded text-xs font-bold ${
                  leg.difficultyRating === 'E' ? 'bg-green-100 text-green-700' :
                  leg.difficultyRating === 'M' ? 'bg-yellow-100 text-yellow-700' :
                  leg.difficultyRating === 'MC' ? 'bg-orange-100 text-orange-700' :
                  'bg-red-100 text-red-700'
                }`}>{leg.difficultyRating}</span>
              </td>
              <td className="px-2 py-1.5 text-center text-gray-400">{leg.waypointCount ?? 0}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
    </div>
  )
}
